import React from 'react';

import { useContacts } from '@context/ContactsContext';
import { useFollowing } from '@context/FollowingContext';
import { useGroups } from '@context/GroupsContext';
import { useRequests } from '@context/RequestsContext';
import { useCurrentUser } from '@context/UsersContext';
import { useLargestGroupsQuery } from '@db/Groups/Queries';
import { useMostFollowedUsersQuery } from '@db/Users/Queries';
import Constants from '@helpers/Constants';
import { createNamedContext, useRequiredContext } from '@helpers/Context';
import { sortBy, sortByDisplayName } from '@helpers/Formatters';
import { auth } from '@helpers/firebase/Initialize';
import { Person, PersonType, User } from '@types';
import { Group } from '@types';

export type RecommendationsContextProps = {
  recommendedFollowUsers: User[];
  recommendedInvitePeople: Person[];
  recommendedGroups: Group[];
};

export const RecommendationsContext = createNamedContext<RecommendationsContextProps>({
  RecommendationsContext: undefined,
});

export function useRecommendations() {
  return useRequiredContext(RecommendationsContext);
}

export function RecommendationsProvider({ children }: React.PropsWithChildren) {
  const recommendedFollowUsers = useRecommendedFollowUsers();
  const recommendedInvitePeople = useRecommendedInvitePeople();
  const recommendedGroups = useRecommendedGroups();

  const value = {
    recommendedFollowUsers,
    recommendedGroups,
    recommendedInvitePeople,
  };

  return (
    <RecommendationsContext.Provider value={value}>{children}</RecommendationsContext.Provider>
  );
}

export function useRecommendedFollowUsers() {
  const { followingUserIds } = useFollowing();
  const { pendingFollowUserRequestUserIds } = useRequests();
  const { blockedUserIds } = useCurrentUser();
  const { users: mostFollowedUsers } = useMostFollowedUsersQuery();
  const currentUserId = auth.currentUser?.uid;

  return React.useMemo(() => {
    const filterUserIds = new Set([
      ...followingUserIds.values(),
      ...pendingFollowUserRequestUserIds.values(),
      ...blockedUserIds.values(),
      ...Constants.joinstar.hiddenUserIds,
      currentUserId,
    ]);
    const filteredUsers = mostFollowedUsers.filter((user) => !filterUserIds.has(user.userId));
    return sortBy(filteredUsers, 'numFollowers', 'desc');
  }, [
    blockedUserIds,
    currentUserId,
    followingUserIds,
    mostFollowedUsers,
    pendingFollowUserRequestUserIds,
  ]);
}

export function useRecommendedInvitePeople() {
  const { contacts } = useContacts();
  const { followingUsers } = useFollowing();

  return React.useMemo(() => {
    const followingPeople = followingUsers.map((user) => ({
      personId: user.userId,
      type: PersonType.USER,
      ...user,
    }));
    const contactsPeople = contacts.map((contact) => ({
      personId: contact.contactId,
      type: PersonType.CONTACT,
      ...contact,
    }));
    return sortByDisplayName([...followingPeople, ...contactsPeople]);
  }, [followingUsers, contacts]);
}

export function useRecommendedGroups() {
  const { pendingJoinGroupRequestGroupIds } = useRequests();
  const { userGroupIds } = useGroups();
  const { groups: largestGroups } = useLargestGroupsQuery();

  return React.useMemo(() => {
    const filteredGroups = largestGroups.filter(
      (group) =>
        !userGroupIds.has(group.groupId) && !pendingJoinGroupRequestGroupIds.has(group.groupId)
    );
    return sortBy(filteredGroups, 'numMembers', 'desc');
  }, [largestGroups, userGroupIds, pendingJoinGroupRequestGroupIds]);
}
